import { useEffect, useMemo, useRef, useState, type DragEvent } from "react";
import { useMutation, useQuery } from "@apollo/client/react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Alert, AlertDescription, Button, ChoiceChips, Container, Eyebrow, Heading, Progress, Text } from "@athena/ui";
import { cn } from "@athena/ui/utils";
import { ArrowLeft, BookPlus, CloudUpload, PartyPopper, RotateCw } from "lucide-react";
import { MyVideoUploadDraftsDocument, TagOptionsDocument } from "@/graphql/media.generated";
import { PresenterOptionsDocument } from "@/graphql/presenters.generated";
import { BrandsDocument } from "@/graphql/brands.generated";
import { CreateTagDocument } from "@/graphql/tags.generated";
import { usePlatformAdmin } from "@/lib/usePlatformAdmin";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { OrganizationSelect } from "@/components/OrganizationSelect";
import {
    canEditDetails,
    isUploaded,
    needsDetails,
    type CopyableField,
    type QueueItem,
    type RejectedFile,
} from "./uploadQueue";
import { useUploadQueue } from "./uploadQueueContext";
import { UploadDropZone } from "./UploadDropZone";
import { UploadQueueRow } from "./UploadQueueRow";
import { VideoDetailsForm } from "./VideoDetailsForm";
import { BuildCourseDialog, type CourseVideo } from "./BuildCourseDialog";

/** A platform admin's destination choice meaning "let the server put it in the shared library". */
const SHARED_LIBRARY = "shared-library";

type QueueFilter = "all" | "needs" | "progress" | "failed" | "done";

const FILTERS: Record<QueueFilter, (item: QueueItem) => boolean> = {
    all: () => true,
    needs: needsDetails,
    progress: (i) => i.phase === "queued" || i.phase === "uploading" || i.phase === "processing",
    failed: (i) => i.phase === "failed" || i.phase === "interrupted",
    done: isUploaded,
};

/** "a.mp4, b.mp4, c.mp4 and 4 more" — a toast, not a manifest. */
const nameList = (files: RejectedFile[]): string => {
    const shown = files.slice(0, 3).map((f) => f.name);
    return files.length > 3 ? `${shown.join(", ")} +${files.length - 3}` : shown.join(", ");
};

export function BulkUploadPage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const queue = useUploadQueue();
    const { items } = queue.state;
    const { summary } = queue;
    const { isPlatformAdmin, ownOrganizationId, loading: meLoading } = usePlatformAdmin();

    const [
        destination,
        setDestination,
    ] = useState<string>(SHARED_LIBRARY);
    const [
        filter,
        setFilter,
    ] = useState<QueueFilter>("all");
    const [
        selectedKey,
        setSelectedKey,
    ] = useState<string | null>(null);
    /** The row whose removal is waiting on a confirmation — a cancel mid-transfer, or a discard. */
    const [
        confirming,
        setConfirming,
    ] = useState<QueueItem | null>(null);
    const [
        buildingCourse,
        setBuildingCourse,
    ] = useState<boolean>(false);
    /** Files dragged over the page. Counted, because crossing a child fires `dragleave` on the parent. */
    const [
        dragDepth,
        setDragDepth,
    ] = useState<number>(0);

    const { data: presentersData } = useQuery(PresenterOptionsDocument);
    const { data: tagsData } = useQuery(TagOptionsDocument);
    const { data: brandsData } = useQuery(BrandsDocument);
    const [createTag] = useMutation(CreateTagDocument, { refetchQueries: [TagOptionsDocument] });
    /**
     * Tag creation is ADMIN/SYSTEM only (`createTag`'s directive), so anyone else is not offered it
     * rather than offered a button the API refuses. The new tag is refetched into every open picker.
     */
    const createTagFromPicker = async (name: string) => {
        try {
            const { data } = await createTag({ variables: { input: { name } } });
            if (data) toast.success(t("admin.bulkUpload.tagCreatedToast", { name: data.createTag.name }));
            return data?.createTag;
        } catch (e) {
            toast.error(t("admin.bulkUpload.tagCreateFailed", { message: (e as Error).message }));
            return undefined;
        }
    };
    /*
     * A brand that sees everything is never stored, so offering it would let an operator "select"
     * something with no effect — the same filter the course editor applies.
     */
    const brandOptions = useMemo(() => (brandsData?.brands ?? []).filter((b) => !b.seesAllContent), [brandsData]);
    // Fresh every visit: a draft finished or discarded in another tab must not come back.
    const { data: draftsData } = useQuery(MyVideoUploadDraftsDocument, { fetchPolicy: "network-only" });

    /*
     * Unfinished uploads from an earlier session join the queue, once per response. Counted here
     * rather than in the reducer because only this page says so out loud — in a toast, which says
     * it once and gets out of the way of the queue.
     */
    const recoveredFrom = useRef<unknown>(null);
    useEffect(() => {
        if (!draftsData || recoveredFrom.current === draftsData) return;
        recoveredFrom.current = draftsData;
        const known = new Set(items.map((i) => i.assetId));
        const recovered = draftsData.myVideoUploadDrafts.filter((d) => !known.has(d.id)).length;
        if (recovered > 0) toast.info(t("admin.bulkUpload.recoveredBanner", { count: recovered }));
        queue.recover(draftsData.myVideoUploadDrafts);
    }, [
        draftsData,
        items,
        queue,
        t,
    ]);

    /*
     * Where new files go. An org admin's own organization; a platform admin chooses, defaulting to
     * the shared library the server already picks for them. Anyone else has nowhere to upload to.
     */
    const organizationId = isPlatformAdmin
        ? destination === SHARED_LIBRARY
            ? undefined
            : destination
        : ownOrganizationId;
    const cannotUpload = !meLoading && !isPlatformAdmin && !ownOrganizationId;

    const visible = useMemo(
        () => items.filter(FILTERS[filter]),
        [
            items,
            filter,
        ],
    );
    const selected = items.find((i) => i.key === selectedKey) ?? null;

    /*
     * Opens the first video needing details when the queue first fills, and moves on when the open
     * video leaves the queue. A row the operator closed stays closed — reopening it under them would
     * make the accordion impossible to collapse.
     */
    const autoOpened = useRef<boolean>(false);
    useEffect(() => {
        if (selected) return;
        if (selectedKey === null && autoOpened.current) return;
        const next = items.find(needsDetails);
        if (next) autoOpened.current = true;
        setSelectedKey(next?.key ?? null);
    }, [
        selected,
        selectedKey,
        items,
    ]);

    const nextNeedingDetails = (afterKey: string): QueueItem | undefined => {
        const start = items.findIndex((i) => i.key === afterKey);
        const ordered = [
            ...items.slice(start + 1),
            ...items.slice(0, Math.max(0, start)),
        ];
        return ordered.find(needsDetails);
    };

    const handleFiles = (files: File[]) => {
        const rejected = queue.addFiles(files, organizationId);
        const added = files.length - rejected.length;
        if (added > 0) toast.success(t("admin.bulkUpload.addedToast", { count: added }));
        const byReason = (reason: RejectedFile["reason"]) => rejected.filter((r) => r.reason === reason);
        const notVideo = byReason("not-a-video");
        const empty = byReason("empty");
        const duplicate = byReason("duplicate");
        if (notVideo.length) toast.warning(t("admin.bulkUpload.rejectedNotVideo", { names: nameList(notVideo) }));
        if (empty.length) toast.warning(t("admin.bulkUpload.rejectedEmpty", { names: nameList(empty) }));
        if (duplicate.length) toast.info(t("admin.bulkUpload.rejectedDuplicate", { names: nameList(duplicate) }));
        // New arrivals should not be hidden behind a filter that excludes them.
        if (added > 0 && filter !== "all" && filter !== "needs") setFilter("all");
    };

    const requestRemove = (item: QueueItem) => {
        // Losing bytes in flight, or an uploaded video, is worth a question. Anything else is a row.
        const destructive = item.phase === "uploading" || (!!item.assetId && !item.published);
        if (destructive) setConfirming(item);
        else void queue.remove(item.key);
    };

    const finishSelected = () => {
        if (!selected) return;
        queue.finish(selected.key);
        const next = nextNeedingDetails(selected.key);
        if (next) setSelectedKey(next.key);
    };

    const copyTargets = selected ? items.filter((i) => i.key !== selected.key && canEditDetails(i)).length : 0;
    const copySelected = () => {
        if (!selected) return;
        // Only what is set: copying an empty presenter would clear the ones already chosen.
        // Presenter and tags only when set — copying an empty one would clear choices already made.
        // Restriction and brands always: "not restricted" and "every brand" are choices too.
        const fields: CopyableField[] = [
            ...(selected.details.presenterId ? (["presenterId"] as const) : []),
            ...(selected.details.tagIds.length > 0 ? (["tagIds"] as const) : []),
            "restricted",
            "brands",
        ];
        queue.copyDetails(selected.key, fields);
        toast.success(t("admin.bulkUpload.copiedToast", { count: copyTargets }));
    };

    const retryAllFailed = () => {
        for (const item of items) if (item.phase === "failed" && item.file) queue.retry(item.key);
    };
    const retryable = items.some((i) => i.phase === "failed" && i.file);
    const allDone = summary.total > 0 && summary.uploaded === summary.total;
    const filterCount = (f: QueueFilter) => items.filter(FILTERS[f]).length;

    const hasQueue = summary.total > 0;
    /*
     * What can become a lesson: anything whose details are saved. Still transcoding is fine — the
     * lesson plays once the video does, as any lesson attached to a processing video would.
     */
    const courseVideos: CourseVideo[] = items.flatMap((i) =>
        i.published && i.assetId
            ? [{ assetId: i.assetId, title: i.details.title.trim() || i.filename, posterUrl: i.posterUrl }]
            : [],
    );
    const buildCourseButton = courseVideos.length > 0 && (
        <Button variant="outline" size="sm" className="w-auto" onClick={() => setBuildingCourse(true)}>
            <BookPlus className="mr-1.5 h-3.5 w-3.5" />
            {t("admin.bulkUpload.buildCourse", { count: courseVideos.length })}
        </Button>
    );
    const uploadDisabled = meLoading || cannotUpload;

    /*
     * The whole page takes a drop once the queue exists, so adding a second batch needs no target.
     * Only for files — a dragged link or text selection is not an upload.
     */
    const carriesFiles = (e: DragEvent) => e.dataTransfer.types.includes("Files");
    const pageDrop = hasQueue && !uploadDisabled;
    const dropHandlers = pageDrop
        ? {
              onDragEnter: (e: DragEvent) => {
                  if (!carriesFiles(e)) return;
                  e.preventDefault();
                  setDragDepth((d) => d + 1);
              },
              onDragOver: (e: DragEvent) => {
                  if (carriesFiles(e)) e.preventDefault();
              },
              onDragLeave: (e: DragEvent) => {
                  if (carriesFiles(e)) setDragDepth((d) => Math.max(0, d - 1));
              },
              onDrop: (e: DragEvent) => {
                  if (!carriesFiles(e)) return;
                  e.preventDefault();
                  setDragDepth(0);
                  handleFiles(Array.from(e.dataTransfer.files));
              },
          }
        : {};

    const summaryParts = [
        summary.transferring > 0 &&
            t("admin.bulkUpload.summaryUploading", {
                count: summary.transferring,
                percent: Math.round(summary.transferFraction * 100),
            }),
        summary.processing > 0 && t("admin.bulkUpload.summaryProcessing", { count: summary.processing }),
        summary.needsDetails > 0 && t("admin.bulkUpload.summaryNeedsDetails", { count: summary.needsDetails }),
        summary.failed > 0 && t("admin.bulkUpload.summaryFailed", { count: summary.failed }),
    ].filter(Boolean);

    return (
        /*
         * On a desktop the page is exactly the window below the app header — the `3rem` is
         * RootLayout's `h-12` bar — and the queue and the details form scroll on their own, so
         * neither pushes the other off screen. On a phone it is an ordinary scrolling page.
         */
        <Container
            maxWidth="2xl"
            className={cn("relative gap-4 p-6", hasQueue && "lg:h-[calc(100dvh-3rem)] lg:overflow-hidden")}
            {...dropHandlers}
        >
            <header className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => navigate("/library")}
                        aria-label={t("admin.bulkUpload.backToLibrary")}
                        title={t("admin.bulkUpload.backToLibrary")}
                    >
                        <ArrowLeft className="h-4 w-4" />
                    </Button>
                    <div>
                        <Eyebrow>{t("admin.bulkUpload.eyebrow")}</Eyebrow>
                        <Heading level={2}>{t("admin.bulkUpload.title")}</Heading>
                    </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    {isPlatformAdmin && (
                        <OrganizationSelect
                            id="bulk-upload-destination"
                            aria-label={t("admin.bulkUpload.destination")}
                            className="w-56"
                            value={destination}
                            onValueChange={setDestination}
                            leadingOptions={[{ value: SHARED_LIBRARY, label: t("admin.bulkUpload.sharedLibrary") }]}
                        />
                    )}
                    {hasQueue && <UploadDropZone variant="button" onFiles={handleFiles} disabled={uploadDisabled} />}
                </div>
            </header>

            {cannotUpload && (
                <Alert variant="destructive">
                    <AlertDescription>{t("admin.bulkUpload.noOrganization")}</AlertDescription>
                </Alert>
            )}

            {!hasQueue && (
                <div className="flex flex-col gap-4">
                    <Text muted>{t("admin.bulkUpload.description")}</Text>
                    <UploadDropZone onFiles={handleFiles} disabled={uploadDisabled} />
                </div>
            )}

            {hasQueue &&
                (allDone ? (
                    <div className="border-success/40 bg-success/10 flex flex-wrap items-center justify-between gap-3 rounded-2xl border px-4 py-3">
                        <span className="flex items-center gap-3">
                            <PartyPopper className="text-success h-5 w-5" />
                            <Text weight="semibold">{t("admin.bulkUpload.allDone", { count: summary.total })}</Text>
                        </span>
                        <span className="flex gap-2">
                            {buildCourseButton}
                            <Button variant="outline" size="sm" onClick={queue.clearFinished}>
                                {t("admin.bulkUpload.clearFinished")}
                            </Button>
                            <Button size="sm" onClick={() => navigate("/library")}>
                                {t("admin.bulkUpload.allDoneAction")}
                            </Button>
                        </span>
                    </div>
                ) : (
                    <div className="border-border bg-card flex flex-col gap-2 rounded-2xl border px-4 py-3">
                        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
                            <Text weight="semibold">
                                {t("admin.bulkUpload.summaryUploaded", {
                                    done: summary.uploaded,
                                    count: summary.total,
                                })}
                            </Text>
                            <span className="flex flex-wrap items-center gap-3">
                                <Text size="sm" muted>
                                    {summaryParts.join(" · ")}
                                </Text>
                                {buildCourseButton}
                            </span>
                        </div>
                        <Progress
                            value={Math.round((summary.uploaded / summary.total) * 100)}
                            className="h-1.5"
                            aria-label={t("admin.bulkUpload.summaryUploaded", {
                                done: summary.uploaded,
                                count: summary.total,
                            })}
                        />
                    </div>
                ))}

            {hasQueue && (
                <section
                    className="flex min-w-0 flex-col gap-3 lg:min-h-0 lg:flex-1"
                    aria-label={t("admin.bulkUpload.title")}
                >
                    <div className="flex flex-wrap items-center justify-between gap-2">
                        <ChoiceChips
                            value={filter}
                            onValueChange={(v) => setFilter(v as QueueFilter)}
                            aria-label={t("admin.bulkUpload.title")}
                            className="gap-1.5"
                            options={(
                                [
                                    [
                                        "all",
                                        "filterAll",
                                    ],
                                    [
                                        "needs",
                                        "filterNeedsDetails",
                                    ],
                                    [
                                        "progress",
                                        "filterInProgress",
                                    ],
                                    [
                                        "failed",
                                        "filterFailed",
                                    ],
                                    [
                                        "done",
                                        "filterDone",
                                    ],
                                ] as const
                            ).map(
                                ([
                                    value,
                                    key,
                                ]) => ({
                                    value,
                                    label: `${t(`admin.bulkUpload.${key}`)} · ${filterCount(value)}`,
                                }),
                            )}
                        />
                        <span className="flex gap-2">
                            {retryable && (
                                <Button variant="outline" size="sm" onClick={retryAllFailed}>
                                    <RotateCw className="mr-1.5 h-3.5 w-3.5" />
                                    {t("admin.bulkUpload.retryAll")}
                                </Button>
                            )}
                            {summary.uploaded > 0 && !allDone && (
                                <Button variant="ghost" size="sm" onClick={queue.clearFinished}>
                                    {t("admin.bulkUpload.clearFinished")}
                                </Button>
                            )}
                        </span>
                    </div>
                    {visible.length === 0 ? (
                        <Text muted size="sm" className="py-10 text-center">
                            {t("admin.bulkUpload.filterEmpty")}
                        </Text>
                    ) : (
                        <ul className="-mr-2 flex flex-col gap-2 pr-2 lg:min-h-0 lg:flex-1 lg:overflow-y-auto">
                            {visible.map((item) => (
                                <UploadQueueRow
                                    key={item.key}
                                    item={item}
                                    selected={item.key === selectedKey}
                                    onToggle={() => setSelectedKey((open) => (open === item.key ? null : item.key))}
                                    onRetry={() => queue.retry(item.key)}
                                    onRemove={() => requestRemove(item)}
                                >
                                    <VideoDetailsForm
                                        item={item}
                                        presenters={presentersData?.presenters ?? []}
                                        tags={tagsData?.tags ?? []}
                                        onCreateTag={isPlatformAdmin ? createTagFromPicker : undefined}
                                        brands={brandOptions}
                                        onEdit={(patch) => queue.editDetails(item.key, patch)}
                                        onFinish={finishSelected}
                                        copyTargets={copyTargets}
                                        onCopy={copySelected}
                                        hasNext={!!nextNeedingDetails(item.key)}
                                        onRetry={() => queue.retry(item.key)}
                                    />
                                </UploadQueueRow>
                            ))}
                        </ul>
                    )}
                </section>
            )}

            {dragDepth > 0 && (
                <div className="border-primary bg-background/85 pointer-events-none absolute inset-2 z-20 flex flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed backdrop-blur-sm">
                    <span className="bg-primary/10 text-primary flex h-16 w-16 items-center justify-center rounded-full">
                        <CloudUpload className="h-8 w-8" />
                    </span>
                    <Text weight="semibold" size="lg">
                        {t("admin.bulkUpload.dropActive")}
                    </Text>
                </div>
            )}

            {buildingCourse && <BuildCourseDialog open onOpenChange={setBuildingCourse} videos={courseVideos} />}

            <ConfirmDialog
                open={confirming !== null}
                onOpenChange={(open) => !open && setConfirming(null)}
                title={
                    confirming?.phase === "uploading"
                        ? t("admin.bulkUpload.cancelTitle")
                        : t("admin.bulkUpload.discardTitle")
                }
                description={
                    confirming?.phase === "uploading"
                        ? t("admin.bulkUpload.cancelDescription", {
                              name: confirming.filename,
                              percent: Math.round(confirming.progress * 100),
                          })
                        : t("admin.bulkUpload.discardDescription", { name: confirming?.filename ?? "" })
                }
                confirmLabel={
                    confirming?.phase === "uploading"
                        ? t("admin.bulkUpload.cancelUpload")
                        : t("admin.bulkUpload.discardUpload")
                }
                dismissLabel={confirming?.phase === "uploading" ? t("admin.bulkUpload.keepUploading") : undefined}
                onConfirm={async () => {
                    const item = confirming;
                    setConfirming(null);
                    if (item) await queue.remove(item.key);
                }}
            />
        </Container>
    );
}
